import { number } from 'yup';
import { Component, OnDestroy, OnInit, ChangeDetectorRef, ElementRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@service/auth.service';
import { ShipyardService } from '@service/shipyard.service';
import { SidebarService } from '@service/sidebar.service';
import { UserPictureService } from '@service/user-picture.service';
import { Shipyard } from 'icreate/common/models/shipyard';
import { Subject, Subscription, takeUntil } from 'rxjs';
import * as _ from 'lodash';
import { ProjectStatus, Vessel } from 'icreate/common/models/vessel';
import { VesselSocketService } from '@service/vessel-socket.service';
import { AccessRequestStatus } from 'icreate/common/models/access-request';
import { RuntimeEnvironments } from '@src/app/models/runtime-enviroment.enum';
import { EnvironmentService } from '@service/environment.service';

@Component({
    selector: 'app-sidebar-menu',
    templateUrl: './sidebar-menu.component.html',
    styleUrls: ['./sidebar-menu.component.css'],
    standalone: false,
})
export class SidebarMenuComponent implements OnInit, OnDestroy {
    @ViewChild('shipyardListElem') shipyardListElem: ElementRef;

    shipyardList: Shipyard[];
    filteredShipyardList: Shipyard[] = [];
    filteredVesselsList: Vessel[] = [];

    selectedShipyardId: string;
    selectedVesselId: string;
    showShipyardVessels: boolean = false;
    showFilteredVessels: boolean = false;
    showVesselOwnerOnly: boolean = false;
    currentUser: { name: string; oid: string; email: string };

    newShipyardSubscription: Subscription;
    parameterSubscription: Subscription;

    profilePicture;

    searchInput: string;

    vesselEditorCounter: Map<string, number> = new Map();
    toolTipMargin: string = '70px';

    private destroySubscriptions$: Subject<void> = new Subject();
    private updateEventListener;
    private env: RuntimeEnvironments;

    constructor(
        private router: Router,
        private shipyardService: ShipyardService,
        private authUserService: AuthService,
        private sidebarService: SidebarService,
        private userPictureService: UserPictureService,
        private vesselSocketService: VesselSocketService,
        private cdr: ChangeDetectorRef,
        private environmentService: EnvironmentService
    ) {
        this.env = this.environmentService.getEnvironment();
    }

    async ngOnInit() {
        this.shipyardList = await this.shipyardService.getList();
        this.newShipyardSubscription = this.shipyardService.listUpdatedSubject().subscribe(() => {
            this.shipyardList = this.shipyardService.getUpdatedList();
        });
        this.startSubscriptions();
        this.currentUser = this.authUserService.getCurrentUser();
        this.profilePicture = await this.userPictureService.getUserPicture(this.currentUser.oid, this.currentUser.name);

        this.updateEditorsCounters();
        this.filterShipyards();
    }

    ngAfterViewInit(): void {
        const element = this.shipyardListElem.nativeElement;
        this.updateEventListener = this.updateMarginTooltip.bind(this);
        element.addEventListener('scroll', this.updateEventListener);
    }

    ngOnDestroy(): void {
        const element = this.shipyardListElem.nativeElement;
        element.removeEventListener('scroll', this.updateEventListener);
        this.destroySubscriptions$.next();
        this.destroySubscriptions$.complete();
    }

    selectShipyard(shipyardId: string) {
        if (this.selectedShipyardId === shipyardId && this.showShipyardVessels) {
            this.showShipyardVessels = false;
            this.selectedVesselId = null;
            this.router.navigateByUrl(`home/${shipyardId}`);
            return;
        } else if (this.selectedShipyardId === shipyardId && !this.showShipyardVessels) {
            this.showShipyardVessels = true;
            return;
        }
        this.router.navigateByUrl(`home/${shipyardId}`);
    }

    truncateName(name: string, max = 25): string {
        if (!name) return '';
        return name.length > max ? name.slice(0, max) + '...' : name;
    }

    goToHomePage() {
        this.selectedShipyardId = null;
        this.selectedVesselId = null;
        this.router.navigateByUrl('home');
    }

    goToSettingsPage() {
        this.selectedShipyardId = null;
        this.selectedVesselId = null;
        this.router.navigateByUrl('home/settings');
    }

    goToDeviceManagmentPage() {
        if (this.isRunningInElectron) return;
        this.selectedShipyardId = null;
        this.selectedVesselId = null;
        this.router.navigateByUrl('home/device-managment');
    }

    goToProtocolsPage() {
        this.selectedShipyardId = null;
        this.selectedVesselId = null;
        this.router.navigateByUrl('home/communication-protocols');
    }

    get isRunningInElectron() {
        return this.env == RuntimeEnvironments.ELECTRON;
    }

    selectVessel(shipyardId: string, vesselId: string) {
        this.router.navigateByUrl(`home/${shipyardId}/${vesselId}`);
    }

    isActiveLink(link: string) {
        if (link && this.selectedShipyardId) {
            return false;
        }
        return this.router.url.includes(link);
    }

    async logout() {
        if (this.env == RuntimeEnvironments.ELECTRON) {
            this.authUserService.setCurrentUser(null, null, null);
            await this.router.navigateByUrl('/electronLogin');
        } else this.authUserService.azureLogOut();
    }

    handleRadioButtonChange(event) {
        this.showVesselOwnerOnly = event.target.value === 'false';
        this.filterShipyards();
    }

    filterShipyards() {
        this.filteredShipyardList = [];
        this.filteredVesselsList = [];
        if (!this.searchInput) {
            this.shipyardList.forEach(shipyard => {
                const foundShipyard = { ...shipyard };
                foundShipyard.vessels = this.filterVessels(shipyard.vessels);
                this.filteredShipyardList.push(foundShipyard);
            });
            return;
        }
        this.shipyardList.forEach(shipyard => {
            const foundShipyard = { ...shipyard };
            const isShipyardMatch = shipyard.name.toLowerCase().includes(this.searchInput.toLowerCase());
            foundShipyard.vessels = this.filterVessels(shipyard.vessels);
            if (isShipyardMatch) {
                this.filteredShipyardList.push(foundShipyard);
            } else {
                this.filteredVesselsList.push(...foundShipyard.vessels);
            }
        });
    }

    private filterVessels(vessels) {
        return vessels.filter((vessel: Vessel) => {
            const isNameMatch = this.searchInput
                ? vessel.name.toLowerCase().includes(this.searchInput.toLowerCase())
                : true;
            const isNumberMatch = this.searchInput ? vessel.number.includes(this.searchInput) : true;
            const isOwnerMatch = !this.showVesselOwnerOnly || vessel.ownerOid === this.currentUser.oid;
            return (isNameMatch || isNumberMatch) && isOwnerMatch;
        });
    }

    getNameArray(name: string): string[] {
        if (!this.searchInput) {
            return [name];
        }

        const search = this.searchInput;
        const regex = new RegExp(`(${this.escapeRegExp(search)})`, 'gi');

        return name.split(regex).filter(Boolean);
    }

    private escapeRegExp(text: string): string {
        return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    getIcon(vessel: Vessel): string {
        if (!vessel) return;
        if (vessel.projectStatus === ProjectStatus.PendingApproval) return 'pending-approval';
        else if (vessel.projectStatus === ProjectStatus.Approved) return 'spaceship';
        else if (!vessel.ownerOid) return 'unlocked';
        else if (vessel.locked) return 'lock-profile';
    }

    getVesselName(vessel: Vessel) {
        return vessel.number + ' - ' + vessel.name;
    }

    getActiveEditors(vessel: Vessel) {
        return this.vesselEditorCounter.get(vessel.id);
    }

    private updateMarginTooltip() {
        if (!this.shipyardListElem) return this.toolTipMargin;
        const scrollTop = this.shipyardListElem.nativeElement.scrollTop;
        const updatedMargin = 70 - scrollTop * 2 + 'px';
        if (updatedMargin !== this.toolTipMargin) {
            this.toolTipMargin = updatedMargin;
            this.cdr.detectChanges();
        }
    }

    private startSubscriptions() {
        this.shipyardService
            .listUpdatedSubject()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(() => {
                this.shipyardList = this.shipyardService.getUpdatedList();
                this.updateEditorsCounters();
                this.filterShipyards();
            });

        this.sidebarService
            .vesselUpdatedSubject()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(({ shipyardId, vesselId }) => {
                this.selectedShipyardId = shipyardId;
                this.showShipyardVessels = true;
                this.selectedVesselId = vesselId;
                this.updateEditorsCounters();
                this.filterShipyards();
                this.cdr.detectChanges();
            });

        this.sidebarService
            .shipyardUpdatedSubject()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(shipyardId => {
                this.selectedShipyardId = shipyardId;
                this.showShipyardVessels = true;
                this.selectedVesselId = null;
                this.filterShipyards();
            });

        this.vesselSocketService
            .vesselUpdated$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe((updatedVessel: Vessel) => {
                this.shipyardList.forEach(shipyard => {
                    const vessels = shipyard.vessels;
                    if (vessels) {
                        vessels.forEach(vessel => {
                            if (vessel.id === updatedVessel.id) {
                                vessel = updatedVessel;
                            }
                        });
                    }
                });
                this.updateEditorsCounters();
                this.filterShipyards();
            });
    }

    private updateEditorsCounters(): void {
        for (const shipyard of this.shipyardList) {
            for (const vessel of shipyard.vessels ?? []) {
                const hasOwner = vessel.ownerOid ? 1 : 0;

                const activeEditors = vessel.VesselAccessRequests?.filter(
                    request => request.requestStatus === AccessRequestStatus.Editing
                );

                let uniqueEditors = 0;
                if (activeEditors && activeEditors.length > 0)
                    uniqueEditors = [...new Set(activeEditors.map(x => x.id))].length;

                this.vesselEditorCounter.set(vessel.id, hasOwner + uniqueEditors);
            }
        }
    }
}
